import React from 'react';
import { WidgetHeaderIcon } from '@/shared/components/ui/WidgetHeaderIcon';
import { Icon } from '@/shared/icons'
interface TopLostReasonsChartProps {
  data: Array<{
    reason: string;
    count: number;
    percentage: number;
  }>;
}

/**
 * Gráfico de motivos de perda de leads
 *
 * Segue o padrão visual dos widgets da dashboard principal:
 * - Header compacto com WidgetHeaderIcon
 * - Legenda inline com dots coloridos
 * - tabular-nums para valores numéricos
 */
export const TopLostReasonsChart: React.FC<TopLostReasonsChartProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div
        className="rounded-lg border p-8 text-center"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <Icon name="alert-circle" className="h-8 w-8 mx-auto mb-2 opacity-30"
          style={{ color: 'var(--theme-muted-text)' }} />
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          Sem motivos de perda registrados
        </p>
      </div>
    );
  }

  // Colors for the bars
  const colors = [
    'var(--theme-error-color)',
    'var(--theme-warning-color)',
    'var(--theme-info-color)',
    'var(--theme-primary, #8b5cf6)',
    'var(--theme-muted-text)',
  ];

  const maxPercentage = Math.max(...data.map(d => d.percentage));

  return (
    <div
      className="rounded-lg border"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header */}
      <div className="px-4 py-3 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h3
              className="text-sm font-semibold"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Principais Motivos de Perda
            </h3>
            <p
              className="text-xs mt-0.5"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Razões mais frequentes
            </p>
          </div>
          <WidgetHeaderIcon icon={'alert-circle'} />
        </div>
      </div>

      {/* Chart */}
      <div className="px-4 pb-4 space-y-3">
        {data.slice(0, 5).map((item, index) => {
          const barWidth = maxPercentage > 0 ? (item.percentage / maxPercentage) * 100 : 0;
          const color = colors[index % colors.length];

          return (
            <div key={item.reason}>
              {/* Label and stats */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span
                    className="text-xs"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {item.reason || 'Sem motivo informado'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className="text-[11px]"
                    style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {item.count} leads
                  </span>
                  <span
                    className="text-xs font-semibold"
                    style={{ color, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {item.percentage.toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Bar */}
              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: 'var(--theme-card-border)' }}
              >
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(barWidth, 2)}%`,
                    backgroundColor: color,
                    opacity: 0.8,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div
        className="px-4 py-2.5 border-t"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            Total de leads perdidos
          </span>
          <span
            className="text-xs font-semibold"
            style={{ color: 'var(--theme-error-color)', fontVariantNumeric: 'tabular-nums' }}
          >
            {data.reduce((acc, d) => acc + d.count, 0)}
          </span>
        </div>
      </div>
    </div>
  );
};

TopLostReasonsChart.displayName = 'TopLostReasonsChart';
